import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DOFINANSOWANIE } from '../../data/dofinansowanie';

// Import zdjęć
import electricalWork1 from '../../assets/images/slider/electrical-work-1.webp';
import electricalWork2 from '../../assets/images/slider/electrical-work-2.webp';
import electricalWork3 from '../../assets/images/slider/electrical-work-3.webp';
import electricalWork4 from '../../assets/images/slider/electrical-work-4.webp';
import electricalWork5 from '../../assets/images/slider/electrical-work-5.webp';

import './ElectricalSlider.css';

const ElectricalSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      skipSnaps: false,
      dragFree: false
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  // Dane slajdów
  const slides = [
    {
      id: 'dofinansowanie',
      type: 'dofinansowanie'
    },
    {
      id: 1,
      image: electricalWork1,
      title: 'Instalacje Elektryczne',
      description: 'Profesjonalne projektowanie i montaż instalacji elektrycznych'
    },
    {
      id: 2,
      image: electricalWork2,
      title: 'Systemy Teletechniczne',
      description: 'Nowoczesne rozwiązania telekomunikacyjne i informatyczne'
    },
    {
      id: 3,
      image: electricalWork3,
      title: 'Automatyka Przemysłowa',
      description: 'Zaawansowane systemy sterowania i automatyzacji'
    },
    {
      id: 4,
      image: electricalWork4,
      title: 'Oświetlenie LED',
      description: 'Energooszczędne rozwiązania oświetleniowe'
    },
    {
      id: 5,
      image: electricalWork5,
      title: 'Serwis i Konserwacja',
      description: 'Kompleksowa obsługa techniczna i naprawy'
    }
  ];

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return (
    <section className="slider-section">
      <div className="container">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((slide) => (
              <div key={slide.id} className="embla__slide">
                <div className="slide-content">
                  {slide.type === 'dofinansowanie' ? (
                    <Link to={DOFINANSOWANIE.path} className="slide-dofinansowanie">
                      <img
                        src={DOFINANSOWANIE.znakiRP}
                        alt="Barwy Rzeczypospolitej Polskiej i godło Rzeczypospolitej Polskiej"
                        className="slide-dofinansowanie__znaki"
                      />
                      <h3 className="slide-dofinansowanie__title">{DOFINANSOWANIE.naglowek}</h3>
                      <p className="slide-dofinansowanie__program">{DOFINANSOWANIE.program}</p>
                      <p className="slide-dofinansowanie__zadanie">{DOFINANSOWANIE.zadanie}</p>
                      <span className="slide-dofinansowanie__more">
                        Szczegóły projektu <ChevronRight size={18} />
                      </span>
                    </Link>
                  ) : (
                    <div className="slide-image-container">
                      <img 
                        src={slide.image} 
                        alt={slide.title}
                        className="slide-image"
                        loading="lazy"
                      />
                      <div className="slide-overlay">
                        <div className="slide-text">
                          <h3>{slide.title}</h3>
                          <p>{slide.description}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          className="embla__button embla__button--prev" 
          onClick={scrollPrev}
          aria-label="Previous slide"
        >
          <ChevronLeft size={24} />
        </button>
        <button 
          className="embla__button embla__button--next" 
          onClick={scrollNext}
          aria-label="Next slide"
        >
          <ChevronRight size={24} />
        </button>

        {/* Dots Navigation */}
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`embla__dot ${
                index === selectedIndex ? 'embla__dot--selected' : ''
              }`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ElectricalSlider;